import { useEffect, useState } from 'react';
import Bugsnag from '@bugsnag/js';
import Parser from 'html-react-parser';
import { ReactComponent as FileUploadIcon } from '../../../assets/icons/file-upload.svg';
import { ReactComponent as FileErrorIcon } from '../../../assets/icons/file-error.svg';
import { ReactComponent as FileCheckIcon } from '../../../assets/icons/file-check.svg';
import { ReactComponent as CancelOutlineIcon } from '../../../assets/icons/cancel-outlined.svg';
import { ReactComponent as TrashIcon } from '../../../assets/icons/trash.svg';
import ButtonCustom from '../__deprecated__/button-custom';
import l from '../../../lang';

import './styles.scss';
import { KycDocument } from '../../../store/company-information/interfaces';

interface KYCUploadFileProps {
  title: string;
  description?: string | JSX.Element | JSX.Element[];
  supportedExtensions: string[];
  initialFile?: File | null;
  maxSize: number;
  disabled?: boolean;
  document?: KycDocument;
  type?: 'idle' | 'success' | 'error';
  externalError?: string;
  onChange: (params: { file: File | null; base64: string | null }) => void;
}

const KYCUploadFile: React.FunctionComponent<KYCUploadFileProps> = ({
  title,
  supportedExtensions,
  maxSize,
  disabled,
  externalError,
  onChange,
  document,
  initialFile = null,
  description,
  children,
}) => {
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<File | null>(initialFile);
  const [internalDisabled, setInternalDisabled] = useState(disabled);
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');
  const [footerMsg, setFooterMsg] = useState('');
  const [type, setType] = useState('idle');

  useEffect(() => {
    if (initialFile) {
      setFile(initialFile);
      setInputValue(initialFile.name);
    }
  }, [initialFile]);

  useEffect(() => {
    if (document) {
      switch (document.status) {
        case 'APPROVED':
          setInternalDisabled(true);
          setType('success');
          setFooterMsg(l('kycUploadFile.documentApproved'));
          break;
        case 'REJECTED':
          setInternalDisabled(false);
          setType('error');
          setFooterMsg(
            l('kycUploadFile.documentRejected') +
              ': ' +
              l(`kycUploadFile.documentRejected.reason.${document.rejected_reason}`)
          );
          break;
        case 'SUBMITTED':
          setInternalDisabled(true);
          setType('warning');
          setFooterMsg(l('kycUploadFile.documentSubmitted'));
          break;
        default:
          setInternalDisabled(disabled);
      }
    } else {
      setInternalDisabled(disabled);
    }
  }, [document, disabled]);

  useEffect(() => {
    if (externalError && !error) {
      setError(externalError);
    }
  }, [externalError]);

  const toBase64 = (f: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(f);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = err => reject(err);
    });

  const handleOnChange = async (files: FileList | null) => {
    setLoading(true);
    setError('');
    setFile(null);
    onChange({ file: null, base64: null });

    const f = files && files[0];
    if (f) {
      const fileExt = /[^.]+$/.exec(`${f.name}`.toLowerCase());
      if (f.size && f.size / 1e6 > maxSize) {
        setError(l('uploadFile.sizeExceeds'));
      } else if (supportedExtensions.filter(ext => ext === (fileExt && fileExt[0])).length === 0) {
        setError(l('uploadFile.notSupported'));
      } else {
        const result = await toBase64(f).catch(e => Error(e));
        if (typeof result === 'string') {
          setFile(f);
          onChange({ base64: result, file: f });
        } else {
          setError(l('uploadFile.error'));
          Bugsnag.notify(new Error('Error to convert the file in base64.'));
        }
      }
    } else {
      setError(l('uploadFile.error'));
    }
    setLoading(false);
  };

  const handleRemove = () => {
    if (!internalDisabled) {
      setError('');
      setFile(null);
      setInputValue('');
      setType('idle');
      onChange({ file: null, base64: null });
    }
  };

  return (
    <div className={`upload-file-kyc upload-file-kyc_${type}`}>
      <div
        className={`upload-file-kyc_content ${error ? 'upload-file-kyc_content_error' : `upload-file-kyc_content_${type}`}`}
      >
        <div className="upload-file-kyc_content_box">
          <div className="upload-file-kyc_content_icon">
            {error || type == 'error' ? <FileErrorIcon /> : file ? <FileCheckIcon /> : <FileUploadIcon />}
          </div>
          <div className="upload-file-kyc_content_title">{title}</div>
        </div>
        <div className="upload-file-kyc-separator"></div>
        <div>
          {file && (
            <div className="upload-file-kyc_content_desc upload-file-kyc_content_desc_success">
              <div className="upload-file-kyc_content_desc_name">
                {file.name}{' '}
                {file && (
                  <div className={'upload-file-kyc_content_desc_success_clean' + (internalDisabled ? ' disabled' : '')}>
                    <CancelOutlineIcon onClick={() => handleRemove()} />
                  </div>
                )}
              </div>
              <div className={'upload-file-kyc_content_desc_remove' + (internalDisabled ? ' disabled' : '')}>
                <TrashIcon onClick={() => handleRemove()} />
              </div>
            </div>
          )}
        </div>
        {!file && (
          <div className="upload-file-kyc_content_footer">
            {description && <div className="upload-file-kyc_content_body_desc">{description}</div>}
            <div className="upload-file-kyc_content_btn">
              <ButtonCustom
                fullWidth
                text={l('uploadFile.label')}
                color="primary"
                variant="outlined"
                size="square"
                disabled={loading || disabled}
                innerComponent={
                  <input
                    type="file"
                    value={inputValue}
                    accept={supportedExtensions
                      .map((t, i) => `.${t}${i + 1 > supportedExtensions.length ? ', ' : ''}`)
                      .join()}
                    style={{ display: 'none' }}
                    onChange={e => {
                      setInputValue(e.target.value);
                      handleOnChange(e.target.files);
                    }}
                  />
                }
                component="label"
              />
            </div>
          </div>
        )}
        {children}
        {error && <div className="upload-file-kyc_content_desc upload-file-kyc_content_desc_error">{error}</div>}
        {!footerMsg && file && <div className="upload-file-kyc_content_success_desc">{l('uploadFile.success')}</div>}
        {footerMsg && type != 'idle' && (
          <div className={`upload-file-kyc_content_desc upload-file-kyc_content_desc_${document?.status?.toLowerCase()}`}>
            <p>{Parser(footerMsg)}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default KYCUploadFile;
